import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { itemsAPI, pokemonAPI, abilitiesAPI, ItemSaleWithDetails, PokemonSaleWithDetails, PriceHistoryResponse } from '../../services/api';
import PriceChart from './PriceChart';
import Autocomplete from '../Helper/Autocomplete';
import SearchableDropdown from '../Helper/SearchableDropdown';
import './DataShowcase.css';

// Pokemon constants
const POKEMON_NATURES = [
  'Adamant', 'Bold', 'Brave', 'Calm', 'Careful', 'Docile', 'Gentle', 'Hardy',
  'Hasty', 'Impish', 'Jolly', 'Lax', 'Lonely', 'Mild', 'Modest', 'Naive',
  'Naughty', 'Quiet', 'Quirky', 'Rash', 'Relaxed', 'Sassy', 'Serious', 'Timid'
];

// Neutral natures that don't affect stats
const NEUTRAL_NATURES = ['Hardy', 'Docile', 'Serious', 'Bashful', 'Quirky'];

// Helper function to display nature names
const formatNatureName = (nature: string): string => {
  return NEUTRAL_NATURES.includes(nature) ? 'Neutral' : nature;
};

const HIDDEN_POWER_TYPES = [
  'Bug', 'Dark', 'Dragon', 'Electric', 'Fighting', 'Fire', 'Flying', 'Ghost',
  'Grass', 'Ground', 'Ice', 'Poison', 'Psychic', 'Rock', 'Steel', 'Water'
];

// Define the shape of our filter state
interface FilterState {
  activeTab: 'items' | 'pokemon';
  priceRange: {
    min: string;
    max: string;
  };
  dateRange: {
    from: string;
    to: string;
  };
  searchQuery: string;
  // Pokemon-specific filters
  nature: string;
  ability: string;
  form: string;
  hiddenPower: string;
  // IV ranges
  atkRange: { min: string; max: string };
  defRange: { min: string; max: string };
  spdRange: { min: string; max: string };
  spatkRange: { min: string; max: string };
  spdefRange: { min: string; max: string };
  hpRange: { min: string; max: string };
}

// Helper function to format price with thousand separators
const formatPrice = (price: number | string): string => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  return numPrice.toLocaleString('en-US');
};

// Helper function to format number input with thousand separators for display
const formatPriceInput = (value: string): string => {
  if (!value) return '';
  // Remove any non-digit characters except decimal point
  const numericValue = value.replace(/[^\d.]/g, '');
  const num = parseFloat(numericValue);
  if (isNaN(num)) return '';
  // Only add commas for numbers >= 1000
  return num >= 1000 ? num.toLocaleString('en-US') : numericValue;
};

// Helper function to parse formatted price input back to raw number
const parsePriceInput = (value: string): string => {
  if (!value) return '';
  // Remove commas and return the numeric string
  const cleaned = value.replace(/,/g, '');
  // Validate it's a valid number
  const num = parseFloat(cleaned);
  return isNaN(num) ? '' : cleaned;
};

// Helper function to format IV display
const formatIVs = (pokemon: PokemonSaleWithDetails): string => {
  const ivLabels = ['ATK', 'DEF', 'SPD', 'SPATK', 'SPDEF', 'HP'];
  const ivs = [
    pokemon.atk_iv,
    pokemon.def_iv,
    pokemon.speed_iv,
    pokemon.spatk_iv,
    pokemon.spdef_iv,
    pokemon.hp_iv
  ];

  // Create formatted IV pairs (e.g., "ATK: 31")
  const formattedIVs = ivs
    .map((iv, index) => iv !== null && iv !== undefined ? `${ivLabels[index]}: ${iv}` : null)
    .filter(iv => iv !== null);

  return formattedIVs.join(' - ');
};

// Custom hook to get Pokemon image URL with sprite table priority
const usePokemonImageUrl = (pokemon: PokemonSaleWithDetails) => {
  const [imageUrl, setImageUrl] = useState<string>('');

  useEffect(() => {
    const getImageUrl = async () => {
      // First priority: Check pokemon_sprites table for custom sprites
      if (pokemon.form && pokemon.form.trim() !== '') {
        try {
          // Use the environment-aware API URL
          const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
            (import.meta.env.MODE === 'production' ? '/api' : `${import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'}/api`);

          const response = await fetch(`${API_BASE_URL}/pokemon/sprite/${pokemon.pokemon_id}/${encodeURIComponent(pokemon.form)}`);
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const spriteData = await response.json();
              if (spriteData.imgur_url) {
                // Convert imgur.com URL to direct image URL
                let directImageUrl = spriteData.imgur_url;
                if (spriteData.imgur_url.includes('imgur.com/') && !spriteData.imgur_url.includes('i.imgur.com')) {
                  const imageId = spriteData.imgur_url.split('/').pop();
                  directImageUrl = `https://i.imgur.com/${imageId}.jpg`;
                }

                setImageUrl(directImageUrl);
                return;
              }
            }
          }
        } catch (error) {
          // Silently handle fetch errors and fall back to default sprites
        }
      }

      // Second priority: Default PokemonDB sprites based on Pokemon name and form
      const pokemonName = pokemon.pokemon_name.toLowerCase();
      const form = pokemon.form?.toLowerCase() || '';

      // Check if it's a shiny form
      const isShiny = form.includes('shiny');

      // Check for regional forms (Alolan, Hisuian, Galarian)
      let regionalUrl = '';

      if (pokemonName.includes('-alola')) {
        const baseName = pokemonName.replace('-alola', '');
        const cleanedName = baseName.replace(/[^a-z0-9-]/g, '');
        regionalUrl = isShiny
          ? `https://img.pokemondb.net/sprites/home/shiny/${cleanedName}-alolan.png`
          : `https://img.pokemondb.net/sprites/home/normal/${cleanedName}-alolan.png`;
        console.log(`Alolan form detected: ${regionalUrl}`);
      } else if (pokemonName.includes('-hisui')) {
        const baseName = pokemonName.replace('-hisui', '');
        const cleanedName = baseName.replace(/[^a-z0-9-]/g, '');
        regionalUrl = isShiny
          ? `https://img.pokemondb.net/sprites/home/shiny/${cleanedName}-hisuian.png`
          : `https://img.pokemondb.net/sprites/home/normal/${cleanedName}-hisuian.png`;
        console.log(`Hisuian form detected: ${regionalUrl}`);
      } else if (pokemonName.includes('-galar')) {
        const baseName = pokemonName.replace('-galar', '');
        const cleanedName = baseName.replace(/[^a-z0-9-]/g, '');
        regionalUrl = isShiny
          ? `https://img.pokemondb.net/sprites/home/shiny/${cleanedName}-galarian.png`
          : `https://img.pokemondb.net/sprites/home/normal/${cleanedName}-galarian.png`;
        console.log(`Galarian form detected: ${regionalUrl}`);
      }

      // If we have a regional form URL, use it
      if (regionalUrl) {
        setImageUrl(regionalUrl);
        return;
      }

      // Default case: Normal or Shiny sprite for base Pokemon
      const cleanedName = pokemonName.replace(/[^a-z0-9-]/g, '');
      if (isShiny) {
        setImageUrl(`https://img.pokemondb.net/sprites/home/shiny/${cleanedName}.png`);
      } else {
        setImageUrl(`https://img.pokemondb.net/sprites/home/normal/${cleanedName}.png`);
      }
    };

    getImageUrl();
  }, [pokemon.pokemon_id, pokemon.form, pokemon.image_url, pokemon.pokemon_name]);

  return imageUrl;
};

// Pokemon row component to handle async image loading
const PokemonRow: React.FC<{ pokemon: PokemonSaleWithDetails }> = ({ pokemon }) => {
  const imageUrl = usePokemonImageUrl(pokemon);

  return (
    <tr key={`pokemon-${pokemon.sale_id}`} className="data-row pokemon-row">
      <td className="item-name">
        <div className="item-display">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={pokemon.pokemon_name}
              className="pokemon-image"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                // If database image fails, try external API
                if (pokemon.image_url && target.src === pokemon.image_url) {
                  target.src = `https://img.pokemondb.net/sprites/home/normal/${pokemon.pokemon_name.toLowerCase().replace(/[^a-z]/g, '')}.png`;
                } else {
                  // If external API also fails, show the same placeholder as items
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjNGE1NTY4Ii8+Cjx0ZXh0IHg9IjMyIiB5PSI0MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjI0IiBmaWxsPSIjZmZmZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj7wn5OmPC90ZXh0Pgo8L3N2Zz4K';
                }
              }}
            />
          ) : (
            <div className="pokemon-image-placeholder">Loading...</div>
          )}
          <span className="item-text">{pokemon.pokemon_name}</span>
        </div>
      </td>
      <td className="price">
        {formatPrice(pokemon.price)}
      </td>
      <td className="date-sold">
        {new Date(pokemon.date_sold).toLocaleDateString()}
      </td>
      <td className="pokemon-details-cell">
        <div className="pokemon-details">
          {/* Row 1: IVs */}
          {(pokemon.atk_iv !== null && pokemon.def_iv !== null) && (
            <div className="detail-row">
              <span className="detail-label">IVs:</span>
              <span className="detail-value iv-display">{formatIVs(pokemon)}</span>
            </div>
          )}

          {/* Row 2: Nature, Ability, Hidden Power, View Proof - all in one line */}
          <div className="detail-row compact-row">
            {pokemon.nature && (
              <span className="compact-detail">
                <span className="detail-label">Nature:</span>
                <span className="detail-value">{formatNatureName(pokemon.nature)}</span>
              </span>
            )}
            {pokemon.ability && (
              <span className="compact-detail">
                <span className="detail-label">Ability:</span>
                <span className="detail-value">{pokemon.ability}</span>
              </span>
            )}
            {pokemon.form && (
              <span className="compact-detail">
                <span className="detail-label">Form:</span>
                <span className="detail-value">{pokemon.form}</span>
              </span>
            )}
            {pokemon.hidden_power && (
              <span className="compact-detail">
                <span className="detail-label">Hidden Power:</span>
                <span className="detail-value">{pokemon.hidden_power}</span>
              </span>
            )}
            {pokemon.proof_url && (
              <a href={pokemon.proof_url} target="_blank" rel="noopener noreferrer" className="browse-data-proof-link compact-proof">
                📸 View Proof
              </a>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

// Helper function to get Item image URL with database priority
const getItemImageUrl = (item: ItemSaleWithDetails): string => {
  // First priority: database image_url
  if (item.image_url) {
    // If it's an Imgur URL, convert it to direct image URL
    if (item.image_url.includes('imgur.com/') && !item.image_url.includes('i.imgur.com')) {
      // Extract image ID from imgur.com/ID format
      const imgurId = item.image_url.split('/').pop();
      const directUrl = `https://i.imgur.com/${imgurId}.png`;
      return directUrl;
    }

    return item.image_url;
  }

  // Fallback: try Serebii with proper formatting
  const itemNameFormatted = item.item_name
    .toLowerCase()
    .replace(/\s+/g, '')     // Remove spaces
    .replace(/[^a-z0-9]/g, ''); // Remove special characters

  const fallbackUrl = `https://serebii.net/itemdex/sprites/${itemNameFormatted}.png`;
  return fallbackUrl;
};

const DataShowcase: React.FC = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState<FilterState>({
    activeTab: 'items',
    priceRange: { min: '', max: '' },
    dateRange: { from: '', to: '' },
    searchQuery: '',
    // Pokemon-specific filters
    nature: '',
    ability: '',
    form: '',
    hiddenPower: '',
    // IV ranges
    atkRange: { min: '', max: '' },
    defRange: { min: '', max: '' },
    spdRange: { min: '', max: '' },
    spatkRange: { min: '', max: '' },
    spdefRange: { min: '', max: '' },
    hpRange: { min: '', max: '' }
  });

  const [loading, setLoading] = useState(false);
  const [itemSales, setItemSales] = useState<ItemSaleWithDetails[]>([]);
  const [pokemonSales, setPokemonSales] = useState<PokemonSaleWithDetails[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [abilities, setAbilities] = useState<{ ability_id: number; ability_name: string }[]>([]);

  // Price history state
  const [priceHistory, setPriceHistory] = useState<PriceHistoryResponse | null>(null);
  const [priceHistoryLoading, setPriceHistoryLoading] = useState(false);
  const [showPriceChart, setShowPriceChart] = useState(false);
  const [chartVisible, setChartVisible] = useState(true); // New state for show/hide toggle
  const [exactSearch, setExactSearch] = useState(false); // Track if search was from autocomplete selection

  // Filter mode state
  const [filterMode, setFilterMode] = useState<'easy' | 'advanced'>('easy');
  const [advancedQuery, setAdvancedQuery] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25); // 25 items per page
  const [totalItems, setTotalItems] = useState(0);

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: 'name' | 'price' | 'date' | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });

  // Load data on component mount and when filters or pagination change
  useEffect(() => {
    fetchData();
  }, [filters, currentPage, itemsPerPage]);

  // Load abilities on component mount
  useEffect(() => {
    const loadAbilities = async () => {
      try {
        const abilitiesData = await abilitiesAPI.getAll();
        setAbilities(abilitiesData);
      } catch (error) {
        console.error('Failed to load abilities:', error);
      }
    };

    loadAbilities();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const baseFilters = {
        status: 'approved', // Only show approved sales
        min_price: filters.priceRange.min ? parseFloat(filters.priceRange.min) : undefined,
        max_price: filters.priceRange.max ? parseFloat(filters.priceRange.max) : undefined,
        date_from: filters.dateRange.from || undefined,
        date_to: filters.dateRange.to || undefined,
        // Server-side pagination
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage
      };

      if (filters.activeTab === 'items') {
        const itemFilters = {
          ...baseFilters,
          item_name: filters.searchQuery || undefined
        };
        const response = await itemsAPI.getAll(itemFilters);

        // Handle new API response format - check if it's paginated response
        const isPaginated = response && typeof response === 'object' && 'data' in response;
        const items = isPaginated ? (response as any).data : response as any[];
        const total = isPaginated ? (response as any).total : items.length;

        // Filter for exact matches if search was from autocomplete selection
        const filteredItems = exactSearch && filters.searchQuery ?
          items.filter((item: any) => item.item_name.toLowerCase() === filters.searchQuery.toLowerCase()) :
          items;

        setItemSales(filteredItems);
        setTotalItems(total);
        setPokemonSales([]); // Clear other tab data
      } else {
        const pokemonFilters = {
          ...baseFilters,
          pokemon_name: filters.searchQuery || undefined,
          nature: filters.nature || undefined,
          ability: filters.ability || undefined,
          form: filters.form || undefined,
          hidden_power: filters.hiddenPower || undefined,
          // IV filters
          min_atk_iv: filters.atkRange.min ? parseInt(filters.atkRange.min) : undefined,
          max_atk_iv: filters.atkRange.max ? parseInt(filters.atkRange.max) : undefined,
          min_def_iv: filters.defRange.min ? parseInt(filters.defRange.min) : undefined,
          max_def_iv: filters.defRange.max ? parseInt(filters.defRange.max) : undefined,
          min_speed_iv: filters.spdRange.min ? parseInt(filters.spdRange.min) : undefined,
          max_speed_iv: filters.spdRange.max ? parseInt(filters.spdRange.max) : undefined,
          min_spatk_iv: filters.spatkRange.min ? parseInt(filters.spatkRange.min) : undefined,
          max_spatk_iv: filters.spatkRange.max ? parseInt(filters.spatkRange.max) : undefined,
          min_spdef_iv: filters.spdefRange.min ? parseInt(filters.spdefRange.min) : undefined,
          max_spdef_iv: filters.spdefRange.max ? parseInt(filters.spdefRange.max) : undefined,
          min_hp_iv: filters.hpRange.min ? parseInt(filters.hpRange.min) : undefined,
          max_hp_iv: filters.hpRange.max ? parseInt(filters.hpRange.max) : undefined
        };
        const response = await pokemonAPI.getAll(pokemonFilters);

        // Handle new API response format - check if it's paginated response
        const isPaginated = response && typeof response === 'object' && 'data' in response;
        const pokemon = isPaginated ? (response as any).data : response as any[];
        const total = isPaginated ? (response as any).total : pokemon.length;

        // Filter for exact matches if search was from autocomplete selection
        const filteredPokemon = exactSearch && filters.searchQuery ?
          pokemon.filter((poke: any) => poke.pokemon_name.toLowerCase() === filters.searchQuery.toLowerCase()) :
          pokemon;

        setPokemonSales(filteredPokemon);
        setTotalItems(total);
        setItemSales([]); // Clear other tab data
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      activeTab: 'items',
      priceRange: { min: '', max: '' },
      dateRange: { from: '', to: '' },
      searchQuery: '',
      // Pokemon-specific filters
      nature: '',
      ability: '',
      form: '',
      hiddenPower: '',
      // IV ranges
      atkRange: { min: '', max: '' },
      defRange: { min: '', max: '' },
      spdRange: { min: '', max: '' },
      spatkRange: { min: '', max: '' },
      spdefRange: { min: '', max: '' },
      hpRange: { min: '', max: '' }
    });
    setAdvancedQuery('');
  };

  const handleTabSwitch = (tab: 'items' | 'pokemon') => {
    handleFilterChange('activeTab', tab);
    // Always reset to easy mode when switching tabs
    setFilterMode('easy');
    // Reset pagination to first page when switching tabs
    setCurrentPage(1);
  };

  const parseAdvancedQuery = (query: string): Partial<FilterState> => {
    const parsedFilters: any = {
      priceRange: { min: '', max: '' },
      dateRange: { from: '', to: '' },
      atkRange: { min: '', max: '' },
      defRange: { min: '', max: '' },
      spdRange: { min: '', max: '' },
      spatkRange: { min: '', max: '' },
      spdefRange: { min: '', max: '' },
      hpRange: { min: '', max: '' }
    };

    // Split by AND/OR and process each condition
    const conditions = query.split(/\s+(AND|OR)\s+/i);

    conditions.forEach(condition => {
      if (condition.toUpperCase() === 'AND' || condition.toUpperCase() === 'OR') return;

      // Parse condition like "pokemon = Charizard" or "price > 1000"
      const match = condition.trim().match(/^(\w+)\s*(=|!=|>=|<=|>|<|LIKE)\s*(.+)$/i);
      if (!match) return;

      const [, field, operator, value] = match;
      const cleanValue = value.trim();

      switch (field.toLowerCase()) {
        case 'pokemon':
        case 'item':
          parsedFilters.searchQuery = cleanValue;
          break;
        case 'nature':
          parsedFilters.nature = cleanValue;
          break;
        case 'ability':
          parsedFilters.ability = cleanValue;
          break;
        case 'form':
          parsedFilters.form = cleanValue;
          break;
        case 'hidden_power':
          parsedFilters.hiddenPower = cleanValue;
          break;
        case 'price':
          if (operator === '>' || operator === '>=') {
            parsedFilters.priceRange.min = cleanValue;
          } else if (operator === '<' || operator === '<=') {
            parsedFilters.priceRange.max = cleanValue;
          } else if (operator === '=') {
            parsedFilters.priceRange.min = cleanValue;
            parsedFilters.priceRange.max = cleanValue;
          }
          break;
        case 'date':
          if (operator === '>' || operator === '>=') {
            parsedFilters.dateRange.from = cleanValue;
          } else if (operator === '<' || operator === '<=') {
            parsedFilters.dateRange.to = cleanValue;
          }
          break;
        // IV filters
        case 'atk_iv':
          if (operator === '>' || operator === '>=') {
            parsedFilters.atkRange.min = cleanValue;
          } else if (operator === '<' || operator === '<=') {
            parsedFilters.atkRange.max = cleanValue;
          }
          break;
        case 'def_iv':
          if (operator === '>' || operator === '>=') {
            parsedFilters.defRange.min = cleanValue;
          } else if (operator === '<' || operator === '<=') {
            parsedFilters.defRange.max = cleanValue;
          }
          break;
        case 'speed_iv':
          if (operator === '>' || operator === '>=') {
            parsedFilters.spdRange.min = cleanValue;
          } else if (operator === '<' || operator === '<=') {
            parsedFilters.spdRange.max = cleanValue;
          }
          break;
        case 'spatk_iv':
          if (operator === '>' || operator === '>=') {
            parsedFilters.spatkRange.min = cleanValue;
          } else if (operator === '<' || operator === '<=') {
            parsedFilters.spatkRange.max = cleanValue;
          }
          break;
        case 'spdef_iv':
          if (operator === '>' || operator === '>=') {
            parsedFilters.spdefRange.min = cleanValue;
          } else if (operator === '<' || operator === '<=') {
            parsedFilters.spdefRange.max = cleanValue;
          }
          break;
        case 'hp_iv':
          if (operator === '>' || operator === '>=') {
            parsedFilters.hpRange.min = cleanValue;
          } else if (operator === '<' || operator === '<=') {
            parsedFilters.hpRange.max = cleanValue;
          }
          break;
      }
    });

    return parsedFilters;
  };

  const applyAdvancedFilter = () => {
    if (!advancedQuery.trim()) {
      alert('Please enter an advanced search query');
      return;
    }

    try {
      const parsedFilters = parseAdvancedQuery(advancedQuery);

      // Apply the parsed filters to current state, merging with existing values
      setFilters(prev => ({
        ...prev,
        searchQuery: parsedFilters.searchQuery || prev.searchQuery,
        nature: parsedFilters.nature || prev.nature,
        ability: parsedFilters.ability || prev.ability,
        form: parsedFilters.form || prev.form,
        hiddenPower: parsedFilters.hiddenPower || prev.hiddenPower,
        priceRange: {
          min: parsedFilters.priceRange?.min || prev.priceRange.min,
          max: parsedFilters.priceRange?.max || prev.priceRange.max
        },
        dateRange: {
          from: parsedFilters.dateRange?.from || prev.dateRange.from,
          to: parsedFilters.dateRange?.to || prev.dateRange.to
        },
        atkRange: {
          min: parsedFilters.atkRange?.min || prev.atkRange.min,
          max: parsedFilters.atkRange?.max || prev.atkRange.max
        },
        defRange: {
          min: parsedFilters.defRange?.min || prev.defRange.min,
          max: parsedFilters.defRange?.max || prev.defRange.max
        },
        spdRange: {
          min: parsedFilters.spdRange?.min || prev.spdRange.min,
          max: parsedFilters.spdRange?.max || prev.spdRange.max
        },
        spatkRange: {
          min: parsedFilters.spatkRange?.min || prev.spatkRange.min,
          max: parsedFilters.spatkRange?.max || prev.spatkRange.max
        },
        spdefRange: {
          min: parsedFilters.spdefRange?.min || prev.spdefRange.min,
          max: parsedFilters.spdefRange?.max || prev.spdefRange.max
        },
        hpRange: {
          min: parsedFilters.hpRange?.min || prev.hpRange.min,
          max: parsedFilters.hpRange?.max || prev.hpRange.max
        }
      }));

      // Show success message instead of switching modes
      alert('Advanced filter applied successfully! Check the results below.');

    } catch (error) {
      alert('Error parsing query. Please check your syntax.');
      console.error('Query parsing error:', error);
    }
  };

  // Autocomplete handlers
  const fetchItemSuggestions = async (query: string) => {
    try {
      const results = await itemsAPI.search(query, 10);
      // Filter results to prioritize exact matches and avoid variants
      const mapped = results.map(item => ({
        id: item.item_id,
        name: item.item_name
      }));

      // Sort by relevance: exact matches first, then startsWith, then contains
      return mapped.sort((a, b) => {
        const aLower = a.name.toLowerCase();
        const bLower = b.name.toLowerCase();
        const queryLower = query.toLowerCase();

        // Exact match gets highest priority
        if (aLower === queryLower) return -1;
        if (bLower === queryLower) return 1;

        // StartsWith gets second priority
        if (aLower.startsWith(queryLower) && !bLower.startsWith(queryLower)) return -1;
        if (bLower.startsWith(queryLower) && !aLower.startsWith(queryLower)) return 1;

        // Shorter names get priority (base version over variants)
        return a.name.length - b.name.length;
      });
    } catch (error) {
      console.error('Error fetching item suggestions:', error);
      return [];
    }
  };

  const fetchPokemonSuggestions = async (query: string) => {
    try {
      const results = await pokemonAPI.search(query, 10);
      // Filter results to prioritize exact matches and avoid variants
      const mapped = results.map(pokemon => ({
        id: pokemon.pokemon_id,
        name: pokemon.pokemon_name
      }));

      // Sort by relevance: exact matches first, then startsWith, then contains
      return mapped.sort((a, b) => {
        const aLower = a.name.toLowerCase();
        const bLower = b.name.toLowerCase();
        const queryLower = query.toLowerCase();

        // Exact match gets highest priority
        if (aLower === queryLower) return -1;
        if (bLower === queryLower) return 1;

        // StartsWith gets second priority
        if (aLower.startsWith(queryLower) && !bLower.startsWith(queryLower)) return -1;
        if (bLower.startsWith(queryLower) && !aLower.startsWith(queryLower)) return 1;

        // Shorter names get priority (base version over variants)
        return a.name.length - b.name.length;
      });
    } catch (error) {
      console.error('Error fetching Pokemon suggestions:', error);
      return [];
    }
  };

  const searchNature = async (query: string): Promise<{ id: number; name: string }[]> => {
    const queryLower = query.toLowerCase();

    // Create unique nature options (combine neutral natures into one "Neutral" option)
    const uniqueNatures: { nature: string; index: number; displayName: string }[] = [];
    const seenDisplayNames = new Set<string>();

    POKEMON_NATURES.forEach((nature, index) => {
      const displayName = formatNatureName(nature);
      if (!seenDisplayNames.has(displayName)) {
        seenDisplayNames.add(displayName);
        uniqueNatures.push({ nature, index, displayName });
      }
    });

    return uniqueNatures
      .filter(({ nature, displayName }) => {
        const natureLower = nature.toLowerCase();
        const displayLower = displayName.toLowerCase();

        // Check if query matches the nature name or display name
        const directMatch = natureLower.includes(queryLower) || displayLower.includes(queryLower);

        // Check if query matches a neutral nature name (like "bashful") and this is the Neutral entry
        const neutralMatch = displayName === 'Neutral' &&
          NEUTRAL_NATURES.some(neutralNature => neutralNature.toLowerCase().includes(queryLower));

        return directMatch || neutralMatch;
      })
      .map(({ index, displayName }) => ({
        id: index,
        name: displayName
      }));
  };

  const searchAbility = async (query: string): Promise<{ id: number; name: string }[]> => {
    const queryLower = query.toLowerCase();

    return abilities
      .filter(ability =>
        ability.ability_name.toLowerCase().includes(queryLower)
      )
      .map(ability => ({
        id: ability.ability_id,
        name: ability.ability_name
      }));
  };

  const handleSearchSelection = (option: { id: number; name: string }) => {
    // Trigger search immediately when user selects from autocomplete
    setExactSearch(true); // Mark as exact search
    handleFilterChange('searchQuery', option.name);
  };

  const fetchPriceHistory = async (name: string, type: 'items' | 'pokemon') => {
    if (!name.trim()) return;

    setPriceHistoryLoading(true);
    try {
      let response: PriceHistoryResponse;
      if (type === 'items') {
        response = await itemsAPI.getPriceHistory(name, 90); // Last 90 days
      } else {
        response = await pokemonAPI.getPriceHistory(name, 90); // Last 90 days
      }
      // Debug log - remove this later
      // console.log('Price history response:', response);
      setPriceHistory(response);
      setShowPriceChart(true); // Show chart even if data is empty
    } catch (err: any) {
      console.error('Error fetching price history:', err);
      // Show empty chart even on error
      setPriceHistory({
        data: [],
        total_records: 0,
        [type === 'items' ? 'item_name' : 'pokemon_name']: name
      });
      setShowPriceChart(true);
    } finally {
      setPriceHistoryLoading(false);
    }
  };

  // Auto-fetch price history when search query changes and enough results exist
  useEffect(() => {
    const totalResults = itemSales.length + pokemonSales.length;
    if (filters.searchQuery.trim() && totalResults > 5) {
      fetchPriceHistory(filters.searchQuery, filters.activeTab);
    } else {
      setShowPriceChart(false);
      setPriceHistory(null);
    }
  }, [filters.searchQuery, filters.activeTab, itemSales.length, pokemonSales.length]);

  // Sorting functions
  const handleSort = (key: 'name' | 'price' | 'date') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (): (ItemSaleWithDetails | PokemonSaleWithDetails)[] => {
    const data = filters.activeTab === 'items' ? itemSales : pokemonSales;

    // With server-side pagination, sorting should be handled by the backend
    // For now, we'll disable client-side sorting and return data as-is
    // TODO: Implement server-side sorting by adding sort parameters to API calls

    return data;
  };

  const getSortIcon = (key: 'name' | 'price' | 'date') => {
    if (sortConfig.key !== key) return ' ↕️'; // Default sort icon
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="data-showcase-container">
      <header className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>📊 Market History</h1>
            <p>Browse auction and trade data from the Pokemon Revolution Online community</p>
          </div>
        </div>
      </header>

      <main className="data-showcase-main">
        {/* Filter Buttons Navigation */}
        <div className="filter-buttons">
          <button
            className={`browse-data-filter-button ${filters.activeTab === 'items' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('items')}
          >
            📦 Items
          </button>
          <button
            className={`browse-data-filter-button ${filters.activeTab === 'pokemon' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('pokemon')}
          >
            🐾 Pokemon
          </button>
        </div>

        <div className="filters-section">
          <div className="filters-header">
            <h3>Filters</h3>
            {/* Temporarily hide mode switch
            {filters.activeTab === 'pokemon' && (
              <button
                className={`mode-toggle-button ${filterMode === 'advanced' ? 'advanced' : 'easy'}`}
                onClick={() => setFilterMode(filterMode === 'easy' ? 'advanced' : 'easy')}
                title={filterMode === 'easy' ? 'Switch to Advanced Mode' : 'Switch to Easy Mode'}
              >
                {filterMode === 'easy' ? '⚡' : '🔧'}
              </button>
            )}
            */}
          </div>

          {filterMode === 'easy' ? (
            /* Easy Mode Filters */
            <>
              {/* First row: Search, Price Min, Price Max, Date Min, Date Max */}
              <div className="filters-grid">
              <div className="filter-group">
                <label>Search</label>
                <Autocomplete
                  placeholder={`Search ${filters.activeTab}...`}
                  value={filters.searchQuery}
                  onChange={(value) => {
                    setExactSearch(false); // Reset exact search when manually typing
                    handleFilterChange('searchQuery', value);
                  }}
                  onSelect={handleSearchSelection}
                  fetchSuggestions={filters.activeTab === 'items' ? fetchItemSuggestions : fetchPokemonSuggestions}
                  className="search-autocomplete"
                />
              </div>

              <div className="filter-group">
                <label>Price Min</label>
                <input
                  type="text"
                  placeholder="Min"
                  className="price-input"
                  value={formatPriceInput(filters.priceRange.min)}
                  onChange={(e) => {
                    const rawValue = parsePriceInput(e.target.value);
                    handleFilterChange('priceRange', { ...filters.priceRange, min: rawValue });
                  }}
                />
              </div>

              <div className="filter-group">
                <label>Price Max</label>
                <input
                  type="text"
                  placeholder="Max"
                  className="price-input"
                  value={formatPriceInput(filters.priceRange.max)}
                  onChange={(e) => {
                    const rawValue = parsePriceInput(e.target.value);
                    handleFilterChange('priceRange', { ...filters.priceRange, max: rawValue });
                  }}
                />
              </div>

              <div className="filter-group">
                <label>Date Min</label>
                <input
                  type="date"
                  value={filters.dateRange.from}
                  onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, from: e.target.value })}
                />
              </div>

              <div className="filter-group">
                <label>Date Max</label>
                <input
                  type="date"
                  value={filters.dateRange.to}
                  onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, to: e.target.value })}
                />
              </div>
            </div>

            {/* Second row: Pokemon-specific filters - only show when Pokemon tab is active */}
            {filters.activeTab === 'pokemon' && (
              <div className="pokemon-filters-row">
                  <div className="filter-group">
                    <SearchableDropdown
                      label="Ability"
                      placeholder="All Abilities"
                      value={filters.ability}
                      onChange={(value) => {
                        handleFilterChange('ability', value || '');
                      }}
                      searchFunction={async (query) => {
                        const results = await searchAbility(query);
                        return results.map(result => ({
                          id: result.id,
                          name: result.name
                        }));
                      }}
                      required={false}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Form</label>
                    <input
                      type="text"
                      placeholder="e.g., Shiny, Normal"
                      value={filters.form}
                      onChange={(e) => handleFilterChange('form', e.target.value)}
                    />
                  </div>

                  <div className="filter-group">
                    <SearchableDropdown
                      label="Nature"
                      placeholder="All Natures"
                      value={filters.nature ? formatNatureName(filters.nature) : ''}
                      onChange={(value) => {
                        // Find the actual nature name from the display name
                        const actualNature = POKEMON_NATURES.find(nature =>
                          formatNatureName(nature) === value || nature === value
                        );
                        handleFilterChange('nature', actualNature || '');
                      }}
                      searchFunction={async (query) => {
                        const results = await searchNature(query);
                        return results.map(result => ({
                          id: result.id,
                          name: result.name
                        }));
                      }}
                      required={false}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Hidden Power</label>
                    <select
                      value={filters.hiddenPower}
                      onChange={(e) => handleFilterChange('hiddenPower', e.target.value)}
                    >
                      <option value="">All Types</option>
                      {HIDDEN_POWER_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* IV Ranges Section */}
                  <div className="filter-group iv-section">
                    <div className="iv-ranges-grid">
                      {/* First Row: ATK, DEF, SPD */}
                      <div className="iv-ranges-grid-row">
                        <div className="iv-range-group">
                          <label>ATK</label>
                          <div className="range-inputs">
                            <input
                              type="number"
                              placeholder="Min"
                              min="0"
                              max="31"
                              value={filters.atkRange.min}
                              onChange={(e) => handleFilterChange('atkRange', { ...filters.atkRange, min: e.target.value })}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              placeholder="Max"
                              min="0"
                              max="31"
                              value={filters.atkRange.max}
                              onChange={(e) => handleFilterChange('atkRange', { ...filters.atkRange, max: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="iv-range-group">
                          <label>DEF</label>
                          <div className="range-inputs">
                            <input
                              type="number"
                              placeholder="Min"
                              min="0"
                              max="31"
                              value={filters.defRange.min}
                              onChange={(e) => handleFilterChange('defRange', { ...filters.defRange, min: e.target.value })}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              placeholder="Max"
                              min="0"
                              max="31"
                              value={filters.defRange.max}
                              onChange={(e) => handleFilterChange('defRange', { ...filters.defRange, max: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="iv-range-group">
                          <label>SPD</label>
                          <div className="range-inputs">
                            <input
                              type="number"
                              placeholder="Min"
                              min="0"
                              max="31"
                              value={filters.spdRange.min}
                              onChange={(e) => handleFilterChange('spdRange', { ...filters.spdRange, min: e.target.value })}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              placeholder="Max"
                              min="0"
                              max="31"
                              value={filters.spdRange.max}
                              onChange={(e) => handleFilterChange('spdRange', { ...filters.spdRange, max: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Second Row: SP.ATK, SP.DEF, HP */}
                      <div className="iv-ranges-grid-row">
                        <div className="iv-range-group">
                          <label>SP.ATK</label>
                          <div className="range-inputs">
                            <input
                              type="number"
                              placeholder="Min"
                              min="0"
                              max="31"
                              value={filters.spatkRange.min}
                              onChange={(e) => handleFilterChange('spatkRange', { ...filters.spatkRange, min: e.target.value })}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              placeholder="Max"
                              min="0"
                              max="31"
                              value={filters.spatkRange.max}
                              onChange={(e) => handleFilterChange('spatkRange', { ...filters.spatkRange, max: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="iv-range-group">
                          <label>SP.DEF</label>
                          <div className="range-inputs">
                            <input
                              type="number"
                              placeholder="Min"
                              min="0"
                              max="31"
                              value={filters.spdefRange.min}
                              onChange={(e) => handleFilterChange('spdefRange', { ...filters.spdefRange, min: e.target.value })}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              placeholder="Max"
                              min="0"
                              max="31"
                              value={filters.spdefRange.max}
                              onChange={(e) => handleFilterChange('spdefRange', { ...filters.spdefRange, max: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="iv-range-group">
                          <label>HP</label>
                          <div className="range-inputs">
                            <input
                              type="number"
                              placeholder="Min"
                              min="0"
                              max="31"
                              value={filters.hpRange.min}
                              onChange={(e) => handleFilterChange('hpRange', { ...filters.hpRange, min: e.target.value })}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              placeholder="Max"
                              min="0"
                              max="31"
                              value={filters.hpRange.max}
                              onChange={(e) => handleFilterChange('hpRange', { ...filters.hpRange, max: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Advanced Mode Filter */
            <div className="advanced-filter">
              <div className="filter-group">
                <label>Advanced Search Query</label>
                <textarea
                  className="advanced-query-input"
                  placeholder={`Examples:
${filters.activeTab === 'pokemon' ?
`- pokemon = Charizard AND nature = Adamant AND price < 50000
- ability = Blaze AND hp_iv >= 25 AND atk_iv >= 30
- level > 50 AND price > 10000 AND price < 100000` :
`- item = Master Ball AND price < 5000
- name LIKE %Potion% AND price > 100
- item != Super Potion AND date > 2024-01-01`}

Operators: =, !=, <, >, <=, >=, LIKE, AND, OR`}
                  value={advancedQuery}
                  onChange={(e) => setAdvancedQuery(e.target.value)}
                  rows={6}
                />
              </div>
              <div className="advanced-help">
                <details>
                  <summary>📖 Advanced Search Help</summary>
                  <div className="help-content">
                    <h4>Available Fields:</h4>
                    {filters.activeTab === 'pokemon' ? (
                      <ul>
                        <li><code>pokemon</code> - Pokemon name</li>
                        <li><code>nature</code> - Pokemon nature</li>
                        <li><code>ability</code> - Pokemon ability</li>
                        <li><code>level</code> - Pokemon level</li>
                        <li><code>hp_iv, atk_iv, def_iv, spatk_iv, spdef_iv, speed_iv</code> - IV values</li>
                        <li><code>price</code> - Sale price</li>
                        <li><code>date</code> - Sale date (YYYY-MM-DD)</li>
                      </ul>
                    ) : (
                      <ul>
                        <li><code>item</code> - Item name</li>
                        <li><code>price</code> - Sale price</li>
                        <li><code>date</code> - Sale date (YYYY-MM-DD)</li>
                      </ul>
                    )}
                    <h4>Operators:</h4>
                    <ul>
                      <li><code>=</code> - Equal</li>
                      <li><code>!=</code> - Not equal</li>
                      <li><code>&lt;, &gt;, &lt;=, &gt;=</code> - Comparison</li>
                      <li><code>LIKE</code> - Pattern matching (use % as wildcard)</li>
                      <li><code>AND, OR</code> - Logical operators</li>
                    </ul>
                  </div>
                </details>
              </div>
            </div>
          )}

          <div className="filter-actions">
            {filterMode === 'advanced' && (
              <button
                onClick={() => applyAdvancedFilter()}
                className="apply-button"
                disabled={!advancedQuery.trim()}
              >
                🔍 Apply Advanced Filter
              </button>
            )}
            <button onClick={resetFilters} className="reset-button">
              Reset Filters
            </button>
          </div>
        </div>

        <div className="results-section">
          <div className="results-header">
            <h3>Results</h3>
            <span className="result-count">
              {itemSales.length + pokemonSales.length} items found
            </span>
          </div>

          {error && (
            <div className="error-message">
              <span>⚠️ {error}</span>
            </div>
          )}

          {loading ? (
            <div className="browse-data-loading">
              <div className="browse-data-loading-spinner"></div>
              <p>Loading data...</p>
            </div>
          ) : (
            <>
              {/* Price Chart Toggle Button */}
              {showPriceChart && priceHistory && (
                <div className="chart-controls">
                  <button
                    onClick={() => setChartVisible(!chartVisible)}
                    className="chart-toggle-button"
                  >
                    {chartVisible ? '📊 Hide Chart' : '📈 Show Chart'}
                  </button>
                </div>
              )}

              {/* Price Chart - Show/Hide based on toggle */}
              {showPriceChart && priceHistory && chartVisible && (
                <PriceChart
                  data={priceHistory.data}
                  title={priceHistory.item_name || priceHistory.pokemon_name || filters.searchQuery}
                  loading={priceHistoryLoading}
                />
              )}

              {itemSales.length === 0 && pokemonSales.length === 0 ? (
                <div className="no-results">
                  <div className="no-results-icon">📭</div>
                  <h4>No sales data found</h4>
                  <p>No approved sales match your current filters. Try adjusting your search criteria or submit some data!</p>
                  <button
                    onClick={() => navigate('/submit-data')}
                    className="submit-data-button"
                  >
                    Submit Data
                  </button>
                </div>
              ) : (
                <>
                  <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        <th
                          className="sortable-header"
                          onClick={() => handleSort('name')}
                        >
                          {filters.activeTab === 'items' ? 'Item Name' : 'Pokemon Name'}{getSortIcon('name')}
                        </th>
                        <th
                          className="sortable-header"
                          onClick={() => handleSort('price')}
                        >
                          Price{getSortIcon('price')}
                        </th>
                        <th
                          className="sortable-header"
                          onClick={() => handleSort('date')}
                        >
                          Date Sold{getSortIcon('date')}
                        </th>
                        {filters.activeTab === 'items' && (
                          <th className="proof-header">
                            Proof
                          </th>
                        )}
                        {filters.activeTab === 'pokemon' && (
                          <th className="pokemon-details-header">
                            Details
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Render sorted data based on active tab */}
                      {getSortedData().map((item) => {
                        if (filters.activeTab === 'items') {
                          const itemData = item as ItemSaleWithDetails;
                          return (
                            <tr key={`item-${itemData.id}`} className="data-row item-row">
                              <td className="item-name">
                                <div className="item-display">
                                  <img
                                    src={getItemImageUrl(itemData)}
                                    alt={itemData.item_name}
                                    className="item-image"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      const currentSrc = target.src;

                                      // If the Imgur direct URL fails, try with .jpg extension
                                      if (currentSrc.includes('i.imgur.com') && currentSrc.endsWith('.png')) {
                                        const jpgUrl = currentSrc.replace('.png', '.jpg');
                                        target.src = jpgUrl;
                                      }
                                      // If .jpg also fails, or if it's another URL, try Serebii fallback
                                      else if (!currentSrc.includes('serebii.net')) {
                                        const itemFormatted = itemData.item_name.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
                                        const serebiiUrl = `https://serebii.net/itemdex/sprites/${itemFormatted}.png`;
                                        target.src = serebiiUrl;
                                      }
                                      // If everything fails, show placeholder
                                      else {
                                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjNGE1NTY4Ii8+Cjx0ZXh0IHg9IjMyIiB5PSI0MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjI0IiBmaWxsPSIjZmZmZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj7wn5OmPC90ZXh0Pgo8L3N2Zz4K';
                                      }
                                    }}
                                  />
                                  <span className="item-text">{itemData.item_name}</span>
                                </div>
                              </td>
                              <td className="price">
                                {formatPrice(itemData.price)}
                              </td>
                              <td>{new Date(itemData.date_sold).toLocaleDateString()}</td>
                              <td className="proof-cell">
                                {itemData.proof_url && (
                                  <a href={itemData.proof_url} target="_blank" rel="noopener noreferrer" className="browse-data-proof-link">
                                    📸 View Proof
                                  </a>
                                )}
                              </td>
                            </tr>
                          );
                        } else {
                          const pokemon = item as PokemonSaleWithDetails;
                          return <PokemonRow key={`pokemon-${pokemon.sale_id}`} pokemon={pokemon} />;
                        }
                      })}
                    </tbody>
                  </table>
                </div>
                </>
              )}

              {/* Pagination Controls */}
              {totalItems > itemsPerPage && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                  </div>
                  <div className="pagination-controls">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Previous
                    </button>
                    <span className="pagination-current">
                      Page {currentPage} of {Math.ceil(totalItems / itemsPerPage)}
                    </span>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                      className="pagination-btn"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.ceil(totalItems / itemsPerPage))}
                      disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                      className="pagination-btn"
                    >
                      Last
                    </button>
                  </div>
                  <div className="items-per-page">
                    <label>
                      Items per page:&nbsp;
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1); // Reset to first page
                        }}
                        className="items-per-page-select"
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default DataShowcase;
